import { makeAutoObservable, runInAction } from 'mobx';
import { User, UserFormValues } from '../models/user';
import { agent } from '../api/agent';
import { store } from './store';
import { router } from '../router/Routes';

class UserStore {
  user: User | null = null;
  fbLoading = false;
  refreshTokenTimeout: NodeJS.Timeout | undefined;

  constructor() {
    makeAutoObservable(this);
  }

  get IsLoggedIn() {
    return !!this.user;
  }

  login = async (creds: UserFormValues) => {
    try {
      const user = await agent.Account.login(creds);
      store.commonStore.setToken(user.token);
      this.startRefreshTokenTimer(user);
      runInAction(() => (this.user = user));
      router.navigate('/activities');
      store.modalStore.closeModal();
    } catch (error) {
      throw error;
    }
  };

  logout = () => {
    store.commonStore.setToken(null);
    this.user = null;
    router.navigate('/');
  };

  getUser = async () => {
    try {
      const user = await agent.Account.current();
      store.commonStore.setToken(user.token);
      runInAction(() => (this.user = user));
      this.startRefreshTokenTimer(user);
    } catch (error) {
      console.error(error);
    }
  };

  register = async (creds: UserFormValues) => {
    try {
      await agent.Account.register(creds);
      router.navigate(`/account/registerSuccess?email=${creds.email}`);
      store.modalStore.closeModal();
    } catch (error: any) {
      if (error?.response?.status === 400) throw error;
      store.modalStore.closeModal();
      console.error(500);
    }
  };

  setImage = (image: string) => {
    if (this.user) this.user.image = image;
  };

  setDisplayName = (name: string) => {
    if (this.user) this.user.displayName = name;
  };

  facebookLogin = async (accessToken: string) => {
    this.fbLoading = true;
    try {
      const user = await agent.Account.fbLogin(accessToken);
      store.commonStore.setToken(user.token);
      this.startRefreshTokenTimer(user);
      runInAction(() => {
        this.user = user;
      });
      router.navigate('/activities');
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.fbLoading = false;
      });
    }
  };

  refreshToken = async () => {
    this.stopRefreshTokenTimer();
    try {
      const user = await agent.Account.refreshToken();
      runInAction(() => {
        this.user = user;
      });
      store.commonStore.setToken(user.token);
      this.startRefreshTokenTimer(user);
    } catch (error) {
      console.error(error);
    }
  };

  private startRefreshTokenTimer = (user: User) => {
    const [_, jwtPayload] = user.token.split('.');
    const jwtToken = JSON.parse(atob(jwtPayload));
    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - 60 * 1000;
    this.refreshTokenTimeout = setTimeout(this.refreshToken, timeout);
  };

  private stopRefreshTokenTimer() {
    if (this.refreshTokenTimeout) {
      clearTimeout(this.refreshTokenTimeout);
    }
  }
}

export { UserStore };
